import { registryTemplateCacheKey } from '#~/device/registry/registry-config-cache.js';
/**
 * E2E renderer test probe — stages a fully-modeled video SOURCE device and
 * drives its calibration status so a hosted (non-HIL) Playwright spec can
 * exercise the "Calibrate camera" config badge tri-state end to end.
 *
 * Why this is distinct from `calibration-probe.ts` (which only writes a
 * device's calibration status, leaving the existing probe byte-identical):
 * the config badge's priority-4 (Calibrate) branch is only reachable, and the
 * badge only RENDERS, for a device that
 *   1. is a VIDEO source — it must be in `room.videoIds` (the sources list is
 *      driven by `videoIds`, which `classify()` derives from `isVideoDevice`),
 *      so the device needs a `video` slice; and
 *   2. carries a NON-GENERIC registry template — otherwise `useConfigBadge`
 *      returns "Select model" at priority 2 and never reaches priority 4; and
 *   3. is a control device with a calibration status that the badge reads; and
 *   4. reports absolute pan AND tilt — a camera that cannot follow a subject is
 *      never offered calibration ('zoom-only' stages that box camera).
 * The calibration status probe supplies none of that (and always writes
 * `calibration: { status }`, so it cannot represent the genuinely-ABSENT
 * "unknown" leg). This fixture writes the device, its `videoIds` membership and
 * a non-generic template in ONE `produce`, and drives calibration through a
 * reported status OR a genuinely-absent subtree ('absent' clears it).
 *
 * NO AGENT REQUIRED. `ConfigureBadge` passes its room to `useConfigBadge`, so a
 * depot-only device renders its badge with no live proxy — this stays a pure
 * depot write, the same shape as calibration-probe.
 *
 * With a single video device, `useArrangement` forces Single, so the source
 * tile is not collapsed and the ConfigureBadge renders.
 *
 * Gated by the SAME preload-bridge `IRIS_E2E_TEST` flag as calibration-probe.ts
 * (`window.electron.process.env` — the renderer's own `process.env` is empty in
 * the packaged app).
 */
import { castId } from '@studio/models';
import { Protocol } from '@studio/models/control';
import { Status } from '@studio/models/control/calibration';
import type { Capabilities } from '@studio/models/control/capabilities';
import { DeviceDefaults } from '@studio/models/device';
import type { Model } from '@studio/models/device';
import type { Agent } from '@studio/models/agent';
import type { Device } from '#~/device/index.js';
import { depot } from '#~/depot/index.js';
import type { Producer } from '#~/depot/index.js';
import type { Template } from '#~/device/registry/index.js';
import type { Room } from '#~/room/index.js';
import {
  buildPlaceholderVideo,
  setTransitoryDevice,
  unsetTransitoryDevice
} from '#~/device/transitory.js';
import type { Discovery } from '#~/device/discovery/index.js';
import type { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';

/**
 * Owner of the injected device. MUST be an id no real agent claims — `syncDevices`
 * deletes a depot device its owner cannot find in its own proxy state. Same
 * convention as calibration-probe's `e2e-calibration-fixture-agent`.
 */
const FIXTURE_AGENT_ID = castId<Agent['id']>('e2e-config-badge-fixture-agent');

/** 'absent' models the add/reload unknown window; a `Status` is a reported state. */
export type ConfigBadgeCalibration = Status | 'absent';

/** Reported movement: a PTZ camera, or a zoom-only box camera that cannot calibrate. */
export type ConfigBadgeMovement = 'ptz' | 'zoom-only';

const AXIS = { absolute: true, continuous: true, position: true };

/** Depot shapes of each movement report; the mapper leaves disabled pan/tilt unset. */
const MOVEMENT_CAPABILITIES: Readonly<Record<ConfigBadgeMovement, Capabilities>> = {
  'ptz': { pan: AXIS, tilt: AXIS, zoom: AXIS },
  'zoom-only': { zoom: AXIS }
};

/** Outcome of one fixture write — mirrors CalibrationProbeWriteReceipt's shape. */
export type ConfigBadgeFixtureReceipt =
  | {
      readonly written: true;
      readonly roomId: string;
      /**
       * Already in the depot. A `false` after the first drive means it was
       * reconciled away and the tile remounted into a fresh 5s reveal.
       */
      readonly reused: boolean;
    }
  | {
      readonly written: false;
      readonly reason: 'no-room-id' | 'room-not-in-depot';
      readonly pathname: string;
    };

/** Fixture inputs: the model to stage and the calibration to drive. */
export interface ConfigBadgeFixtureSpec {
  readonly modelId: string;
  /** Use a name without "other" unless exercising the legacy generic-name fallback. */
  readonly modelName: string;
  readonly calibration: ConfigBadgeCalibration;
  /** Defaults to 'ptz' so the Calibrate branch stays reachable. */
  readonly movement?: ConfigBadgeMovement;
  /** Drive the same fields written by protobuf device mapping, without an agent. */
  readonly videoAuthenticationStatus?: AuthenticationStatus;
  readonly controlAuthenticationStatus?: AuthenticationStatus;
  readonly generic?: boolean;
  /** Registry brand of the staged model; DefaultBrandId stages "Other – brand is not listed". */
  readonly brandId?: string;
  readonly placeholder?: boolean;
  readonly placeholderKind?: 'onvif' | 'iris';
  readonly embeddedDiscoveryOwner?: boolean;
}

/**
 * Collaborators the fixture needs, injected so the depot-write contract is
 * unit-testable with plain doubles and no module mocks.
 */
export interface ConfigBadgeFixtureDeps {
  /** Depot producer accessor; read per call, not once at install. */
  readonly getProduce: () => Producer;
}

// Matches `/room/:roomId` — same pattern the calibration probe keys off.
const ACTIVE_ROOM_PATH = /^\/room\/([^/]+)/;

const resolveActivePathname = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

const resolveActiveRoomId = (pathname: string): string | undefined =>
  ACTIVE_ROOM_PATH.exec(pathname)?.[1];

/**
 * Renderer-process gate — read through the preload bridge
 * (`window.electron.process.env`), NOT bare `process.env` (empty in the
 * packaged renderer). Same bridge the calibration/device probes read
 * `IRIS_E2E_TEST` through.
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/**
 * Build the fixture writer against injected collaborators. Factored out (like
 * `createCalibrationSetStatus`) so the depot-write contract is unit-testable
 * with fakes; the install side effect below stays IRIS_E2E_TEST-gated.
 *
 * Stages in one `produce`: the non-generic template, the video source device
 * (control + calibration, or a cleared subtree), and its `videoIds` membership.
 * Returns a receipt so the spec can distinguish "wrote into the live room" from
 * each way it can be dropped.
 */
export const createConfigBadgeFixture =
  (deps: ConfigBadgeFixtureDeps) =>
  (deviceId: string, spec: ConfigBadgeFixtureSpec): ConfigBadgeFixtureReceipt => {
    const pathname = resolveActivePathname();
    const activeRoomId = resolveActiveRoomId(pathname);
    if (!activeRoomId) {
      return { written: false, reason: 'no-room-id', pathname };
    }

    const roomId = castId<Room['id']>(activeRoomId);
    const id = castId<Device['id']>(deviceId);

    let written = false;
    let reused = false;
    deps.getProduce()((draft) => {
      const room = draft.rooms[roomId];
      if (!room) {
        return;
      }
      // This test-owned discovery owner has no proxy and never links real hardware.
      if (spec.placeholder && spec.embeddedDiscoveryOwner) {
        draft.agents[FIXTURE_AGENT_ID] = {
          id: FIXTURE_AGENT_ID,
          name: 'Fixture embedded Bridge',
          roomId,
          embedded: true,
          status: 'offline-embedded',
          version: 'fixture'
        };
      } else {
        delete draft.agents[FIXTURE_AGENT_ID];
      }
      const modelId = castId<Model['id']>(spec.modelId);
      // Non-generic template so useConfigBadge's priority-2 (Select model)
      // passes and the priority-4 (Calibrate) branch is reachable. The badge
      // reads only `template.model` name/generic/brandId, so a minimal
      // template satisfies it.
      draft.registry.templates[registryTemplateCacheKey(modelId)] = {
        model: { name: spec.modelName, generic: spec.generic, brandId: spec.brandId }
      } as unknown as Template;

      const existing = room.devices[id];
      reused = existing !== undefined;
      const device: Device = {
        ...DeviceDefaults,
        ...existing,
        id,
        modelId,
        videoAuthenticationStatus: spec.videoAuthenticationStatus,
        controlAuthenticationStatus: spec.controlAuthenticationStatus,
        // Keeps the device out of every agent's reconciliation sweep.
        agentId: FIXTURE_AGENT_ID,
        // A `video` slice makes the device a video source (isVideoDevice →
        // classify → room.videoIds), so it renders a source tile. The tile
        // renders regardless of the video's contents; a minimal slice is enough.
        video: existing?.video ?? ({} as Device['video']),
        control: {
          ...existing?.control,
          config: existing?.control?.config ?? {
            protocol: Protocol.HTTP,
            host: 'iris-e2e-fixture.local',
            port: 0
          },
          // 'absent' clears the calibration subtree — the add/reload "unknown"
          // window the badge must treat as unknown, NOT "never calibrated". A
          // Status writes a reported state (NULL shows the badge; OK clears it).
          calibration: spec.calibration === 'absent' ? undefined : { status: spec.calibration },
          // Same depot field protobuf mapping fills from the status capability report.
          capabilities: MOVEMENT_CAPABILITIES[spec.movement ?? 'ptz']
        }
      };
      room.devices[id] = device;
      // classify only re-runs on a sync tx, so push now. FIXTURE_AGENT_ID is
      // what keeps the device across later syncs.
      if (!room.videoIds.includes(id)) {
        room.videoIds = [...room.videoIds, id];
      }
      written = true;
    });
    // Exercise the real placeholder store: these devices never belong to room.devices.
    if (written && spec.placeholder) {
      deps.getProduce()((draft) => {
        delete draft.rooms[roomId]!.devices[id];
        draft.rooms[roomId]!.videoIds = draft.rooms[roomId]!.videoIds.filter(
          (value) => value !== id
        );
      });
      setTransitoryDevice({
        id,
        name: 'Authentication required camera',
        transitoryKind: spec.placeholderKind ?? 'onvif',
        discoveryId: id as unknown as Discovery['id'],
        originalDiscovery: {
          id,
          name: 'Authentication required camera',
          address: '192.0.2.1',
          ...(spec.embeddedDiscoveryOwner ? { agentId: FIXTURE_AGENT_ID } : {})
        } as unknown as Discovery,
        address: '192.0.2.1',
        video: buildPlaceholderVideo('192.0.2.1'),
        createdAt: Date.now()
      });
    } else if (written) {
      unsetTransitoryDevice(id);
    }
    return written
      ? { written: true, roomId: activeRoomId, reused }
      : { written: false, reason: 'room-not-in-depot', pathname };
  };

if (isE2eEnabled()) {
  const setup = createConfigBadgeFixture({
    getProduce: () => depot.getState().produce
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSetupConfigBadgeDevice = setup;
  // Cleanup only the test-owned device; never sweep other sources in the shared room.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisRemoveConfigBadgeDevice = (deviceId: string): void => {
    const id = castId<Device['id']>(deviceId);
    unsetTransitoryDevice(id);
    const roomId = resolveActiveRoomId(resolveActivePathname());
    depot.getState().produce((draft) => {
      delete draft.agents[FIXTURE_AGENT_ID];
      const room = roomId ? draft.rooms[castId<Room['id']>(roomId)] : undefined;
      if (!room) return;
      const modelId = room.devices[id]?.modelId;
      if (modelId) {
        delete draft.registry.templates[registryTemplateCacheKey(modelId)];
      }
      delete room.devices[id];
      room.videoIds = room.videoIds.filter((value) => value !== id);
    });
  };
}
